/**
 * `SitesListSkeleton` — rendered on the first mount of the Sites list
 * while the initial `sites.list()` is in flight (founder 2026-10-20).
 *
 * Replaces the previous "No sites added" flash: an unauthenticated or
 * first-render pass used to paint the empty + "Add your first site"
 * row for ~1 round-trip before the real list arrived, suggesting the
 * merchant had no sites when they did. The skeleton holds the layout
 * (header, three card placeholders) at the same rhythm as the final
 * grid so there is no shift on hand-off.
 *
 * Monochrome ladder — no lime / amber / coral. Mirrors the pattern
 * used by `apps/dashboard/src/views/readiness/LoadingSkeleton.tsx`.
 */

import { useC } from '../../lib/theme.js';
import { NumLabel } from '../../components/atoms/index.js';

const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export function SitesListSkeleton(): JSX.Element {
  const C = useC();
  const bar = (w: number, h = 10): React.CSSProperties => ({
    height: h,
    width: w,
    maxWidth: '100%',
    background: C.surface2,
    borderRadius: 2,
  });
  const card: React.CSSProperties = {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 4,
    minHeight: 240,
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  };
  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10"
      aria-busy="true"
      aria-label="Loading your sites"
      data-testid="sites-list-loading"
    >
      <div className="flex items-end justify-between mb-8 sm:mb-10 flex-wrap gap-4">
        <div>
          <NumLabel n={0}>your sites · loading</NumLabel>
          <h1
            style={{
              fontFamily: serifFont,
              fontSize: 'clamp(36px, 6vw, 56px)',
              lineHeight: 1,
              color: C.text,
              letterSpacing: -1.5,
            }}
          >
            All your sites,
            <br />
            <em>one front door</em>.
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ ...bar(10, 10), borderRadius: '50%' }} />
              <div style={bar(90, 8)} />
            </div>
            <div style={bar(220, 22)} />
            <div style={bar(140, 10)} />
            <div style={{ display: 'flex', gap: 16, marginTop: 'auto' }}>
              <div style={bar(60, 8)} />
              <div style={bar(60, 8)} />
              <div style={bar(60, 8)} />
            </div>
          </div>
        ))}
      </div>

      <div
        className="mt-6 flex items-center gap-2"
        style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}
      >
        <span style={{ ...bar(140, 8) }} />
      </div>
    </div>
  );
}
